import { MutableRefObject, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { TimeBasedVisualizationConfig, TimeBasedVisualizationConfigJSON } from "../Types/TimeBasedVisualizationConfig"
import { currentPatientFileInfoAtom } from "../Atoms/PatientFile"
import { RecoilState, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { useModalProvider } from "../../../../../Providers/ModalProvider"
import { ReactCallbacks } from "../Types/ReactCallbacks"
import { linkedWindowsTimelineControllerAtom } from "../Atoms/Timeline"
import { D3TimeBasedVisualization } from "../Components/Visualizations/D3TimeBasedVisualization"
import { TimeSeriesPageManager } from "../Data/TimeSeriesPageManager"
import { DimensionsContext } from "../../../../../Providers/DimensionsProvider"
import { useOnMount } from "../../../../../Hooks/useOnMount"
import { ScaleTime } from "d3"
import { isEqual } from "lodash"
import { useD3SyncTime } from "./useD3SyncTime"
import { LinkedWindowEventType, WindowClickedEvent } from "../Types/LinkedWindowInfo"
import { allWindowsDispatch } from "../Atoms/Visualizations"
import { layoutGroupsAtom, selectedLayoutGroupAtom, selectedLayoutIdAtom } from "../Atoms/Layout"
import { VisualizationComponent } from "../VisualizationComponent"
import { useD3UpdateRecordingInfo } from "./useD3UpdateRecordingInfo"
import { useEnvironmentVariablesProvider } from "../../../../../Providers/EnvironmentVariablesProvider"
import { D3ModalityGraphGroup } from "../Components/Visualizations/TimeSeriesGraphGroup/D3/D3ModalityGraphGroup"
import { clearWindowCommands, consumeWindowCommand } from "../Utils/windowCommandRegistry"
import { EEG_WPS_SPEED_OPTIONS } from "../Types/EEG"
import { useUpdateWindowProgress } from "./useWindowLoadingState"

// Find the "missing" or "differently typed" properties from B when compared to A
type Difference<A, B> = Pick<B, { [K in keyof B]: K extends keyof A ? (A[K] extends B[K] ? never : K) : K }[keyof B]>

type RequiredVisualizationProps = {
	id: string
	windowType: VisualizationComponent
	viewScale: ScaleTime<any, any, any>
	fileScale: ScaleTime<any, any, any>
}

type DefaultVisualizationProps = {
	playbackSpeed: number
	liveModeEnabled: boolean
	isLinked: boolean
	hasInitialized: boolean
}

type SharedVisualizationProps = Omit<TimeBasedVisualizationConfig, keyof (RequiredVisualizationProps & DefaultVisualizationProps)>

type useD3ConfigProps<
	ControllerType extends D3TimeBasedVisualization<ConfigType, ReactCallbacks<ConfigType>, any, any>,
	ConfigType extends TimeBasedVisualizationConfig,
	JSONType extends TimeBasedVisualizationConfigJSON,
	PageManagerType extends TimeSeriesPageManager<any, any>
> = {
	atom: RecoilState<ConfigType>
	d3ControllerConstructor: (node: HTMLDivElement, config: ConfigType, pageManager: PageManagerType, callbacks: ReactCallbacks<ConfigType>) => ControllerType
	extractJSON: (config: ConfigType) => JSONType
	nodeRef: MutableRefObject<HTMLDivElement | null>
	props: Omit<Difference<JSONType, ConfigType>, keyof (SharedVisualizationProps & DefaultVisualizationProps)> // All properties in the config that are not set by JSON
	initProps: JSONType
	pageManager: PageManagerType
}

export const useD3Controller = <
	ControllerType extends D3TimeBasedVisualization<ConfigType, any, any, any>,
	ConfigType extends TimeBasedVisualizationConfig,
	JSONType extends TimeBasedVisualizationConfigJSON,
	PageManagerType extends TimeSeriesPageManager<any, any>
>({
	atom,
	nodeRef,
	props,
	initProps,
	pageManager,
	d3ControllerConstructor,
	extractJSON
}: useD3ConfigProps<ControllerType, ConfigType, JSONType, PageManagerType>) => {
	const { dataSourceMap } = useRecoilValue(currentPatientFileInfoAtom)
	const [rootAtomConfig, setRootAtomConfig] = useRecoilState(atom)
	const { createModal } = useModalProvider()
	const [timelineController, setLinkedWindowsTimelineController] = useRecoilState(linkedWindowsTimelineControllerAtom)
	const [d3Controller, setD3Controller] = useState<ControllerType | undefined>()
	const dimensions = useContext(DimensionsContext)
	const { patientId, timeZone, isAdmitted } = useRecoilValue(currentPatientFileInfoAtom)
	const setLayoutGroups = useSetRecoilState(layoutGroupsAtom)
	const previousD3Config = useRef<ConfigType>()
	const previousAtomKey = useRef<string>()
	const previousViewDomain = useRef<string>()
	const selectedLayoutGroup = useRecoilValue(selectedLayoutGroupAtom)
	const selectedLayoutId = useRecoilValue(selectedLayoutIdAtom)
	const { environmentVariables } = useEnvironmentVariablesProvider()
	const { updateProgress } = useUpdateWindowProgress(atom.key)

	const setRootConfig = useCallback((newValueOrUpdater: ConfigType | ((previous: ConfigType) => ConfigType)) => {
		// Technically, the update needs to happen in two places.
		// First, the atom that contains the component.
		// This is the smaller change. The one that only affects the display that we are working on.
		setRootAtomConfig(newValueOrUpdater)

		// Most of the time, we actually also need to update the deep layout JSON too. This has been the cause of some difficult to find bugs.
		// Like when you configure the same component in the same area on a new display. It caused a de-sync between the component atoms
		// and the layoutGroups atom unless we update both of them.
		setLayoutGroups(previous => previous.map(displayGroup => {
			if (displayGroup.id === selectedLayoutGroup?.id) {
				return {
					...displayGroup,
					layouts: displayGroup.layouts.map(layout => {
						if (layout.id === selectedLayoutId) {
							return {
								...layout,
								areas: layout.areas.map(area => {
									if (area.area === rootAtomConfig.id) {
										let newProps: ConfigType;

										if (newValueOrUpdater instanceof Function) {
											newProps = newValueOrUpdater((area.props ?? {}) as ConfigType)
										} else {
											newProps = newValueOrUpdater
										}

										return { ...area, props: extractJSON(newProps) }
									}

									return area
								}) 
							}
						}
						return layout
					})
				}
			}
			return displayGroup
		}))
	}, [extractJSON, rootAtomConfig.id, selectedLayoutGroup?.id, selectedLayoutId, setLayoutGroups, setRootAtomConfig])

	// Functions and context that all D3 components may need access to.
	const reactCallbacks: ReactCallbacks<ConfigType> = useMemo(
		() => ({
			createModal,
			setRootConfig,
			setLinkedWindowsTimelineController,
			updateWindowLoadingProgress: updateProgress,
			dataSourceMap
		}),
		[createModal, dataSourceMap, setLinkedWindowsTimelineController, setRootConfig, updateProgress]
	)

	// Creates the D3 configuration needed to render the D3 component.
	const d3Config = useMemo(() => {
		const base = { ...rootAtomConfig }

		if (!rootAtomConfig.hasInitialized) {
			Object.assign(base, initProps)
			base.hasInitialized = true
		}

		if (base.windowType === VisualizationComponent.EEG_MONTAGE) {
			base.playbackMode = "wps"

			const minWpsSpeed = EEG_WPS_SPEED_OPTIONS[0]
			const maxWpsSpeed = EEG_WPS_SPEED_OPTIONS[EEG_WPS_SPEED_OPTIONS.length - 1]
			const clampedSpeed = Math.min(Math.max(base.playbackSpeed ?? minWpsSpeed, minWpsSpeed), maxWpsSpeed)

			base.playbackSpeed = EEG_WPS_SPEED_OPTIONS.reduce((closest, option) => {
				const currentDiff = Math.abs(option - clampedSpeed)
				const closestDiff = Math.abs(closest - clampedSpeed)

				if (currentDiff < closestDiff) {
					return option
				}

				if (currentDiff === closestDiff) {
					return option > closest ? option : closest
				}

				return closest
			}, minWpsSpeed)
		}

		// The shared visualization props are not stored in the layout JSON, and are shared across all visualization types.
		const shared: SharedVisualizationProps = {
			dimensions,
			timelineController,
			patientId,
			timeZone,
			patientIsAdmitted: isAdmitted,
			environmentVariables
		}

		return { ...base, ...shared, ...props }
	}, [dimensions, environmentVariables, initProps, isAdmitted, patientId, props, rootAtomConfig, timeZone, timelineController])

	const commandWindowId = useMemo(() => ({ layoutId: selectedLayoutId as string, windowId: d3Config.id }), [d3Config.id, selectedLayoutId])

	// This hook gets called the first time the React component renders on the screen.
	// It returns a function which will get called when the React component leaves the screen.
	useOnMount(() => {
		if (!nodeRef.current) {
			return
		}

		// If the patient is admitted, enable live mode and initialize the page manager with the correct state
		d3Config.liveModeEnabled = isAdmitted
		d3Config.isLinked = !isAdmitted

		const controller = d3ControllerConstructor(nodeRef.current, d3Config, pageManager, reactCallbacks)

		setD3Controller(controller)
		setRootAtomConfig(d3Config)

		return () => {
			clearWindowCommands(commandWindowId)
			controller.unmount()
		}
	})

	useEffect(() => {
		const clickHandler = () => {
			const event: WindowClickedEvent = { type: LinkedWindowEventType.CLICKED, controller: d3Config.id, lastHoveredDate: d3Controller?.getLastHoveredDate()}
			allWindowsDispatch.call("click", undefined, event)		
		}
		nodeRef.current?.addEventListener("click", clickHandler)
		return () => document.removeEventListener("click", clickHandler)
	}, [d3Config.id, d3Controller, nodeRef])

	// When the config changes, update the D3 component tree and re-render.
	useEffect(() => {
		const isSameAtom = !previousAtomKey.current || previousAtomKey.current === atom.key 
		const viewDomain = d3Config.viewScale.domain()
		const viewDomainKey = `${viewDomain[0].getTime()}:${viewDomain[1].getTime()}`
		const didViewDomainChange = previousViewDomain.current !== undefined && previousViewDomain.current !== viewDomainKey

		if (d3Controller && dimensions.height > 0 && isSameAtom && (!isEqual(previousD3Config.current, d3Config) || didViewDomainChange)) {
			d3Controller.updateConfig(d3Config)

			if (didViewDomainChange) {
				d3Controller.viewTimesChanged(viewDomain[0].getTime(), viewDomain[1].getTime())

				if (
					consumeWindowCommand(commandWindowId, "autoscaleAfterNextViewChange") &&
					d3Controller instanceof D3ModalityGraphGroup
				) {
					d3Controller.autoScale()
				}
			}

			setRootAtomConfig(d3Config)
			previousD3Config.current = d3Config
			previousAtomKey.current = atom.key
			previousViewDomain.current = viewDomainKey
		}
	}, [atom.key, commandWindowId, d3Config, d3Controller, dimensions.height, setRootAtomConfig])

	useD3SyncTime({ d3Controller })

	useD3UpdateRecordingInfo({ d3Controller })

	return d3Controller
}
